import React, { useEffect, useMemo } from 'react';
import { CreateNoteButton } from '@/components/buttons/viewHeaderButtons/CreateNoteButton';
import { TabView } from '@/components/layouts/TabView';
import { CreateTaskButton } from '@/components/buttons/viewHeaderButtons/CreateTaskButton';
import { ClientBrandIcon } from '@/components/icons/ClientBrandIcon';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { getClientTabs } from '@/app/tabs';
import { addRecentlyViewed } from '@/features/userPrefs/userPrefsSlice';
import { useGetClientContactData } from '@/hooks/useGetClientContactData';
import { ClientIcon } from '@/components/icons/Client';
import { ProspectsIcon } from '@/components/icons/ProspectsIcon';
import { useTheme } from '@emotion/react';
import WorkspaceContextProvider from '@/context/WorkspaceContext';

const ICON_HEIGHT = '55px';
const ICON_PROPS = {
	height: ICON_HEIGHT,
	width: ICON_HEIGHT,
};

export const ClientTabView = () => {
	const theme = useTheme();
	const location = useLocation();
	const { clientId } = useParams();
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { clientRow } = useObjectContext();
	const isProspect = clientRow?.isProspect;
	const dispatch = useDispatch();

	useEffect(() => {
		if (userDetails?.userId && clientRow?.name) {
			dispatch(
				addRecentlyViewed({
					userId: userDetails.userId,
					type: 'clients',
					data: {
						id: clientRow.id,
						name: clientRow.name,
						path: location.pathname,
						isProspect,
					},
				}),
			);
		}
	}, [userDetails?.userId, clientRow?.name, clientRow?.id, location.pathname, isProspect]);

	const { getFeatureFlags } = useFeatureFlagContext();
	const showInsightClientPolicies = Boolean(getFeatureFlags('displayClientPolicies')?.active);

	const { logoDomain, mainContact, subtitle } = useGetClientContactData({
		clientRow,
		clientId,
	});

	const headerComponents = {
		notes: CreateNoteButton,
		tasks: CreateTaskButton,
	};

	const tabs = useMemo(() => {
		const pathname = location.pathname.split(`/${clientId}`);
		let clientTabs = getClientTabs(pathname).map((tab) => {
			return {
				...tab,
				HeaderComponent: headerComponents[tab.value],
			};
		});
		if (!showInsightClientPolicies || clientRow?.isProspect) {
			clientTabs = clientTabs.filter((tab) => tab.value !== 'policies');
		}
		return clientTabs;
	}, [headerComponents]);

	const fallbackIconProps = {
		...ICON_PROPS,
		...(theme.palette.mode === 'dark' && { bgColor: 'transparent' }),
		...(!theme.palette.mode === 'dark' && !isProspect && { bgColor: '#34495E' }),
	};

	return (
		<WorkspaceContextProvider>
			<TabView
				objectType='client'
				tabs={tabs}
				icon={
					<ClientBrandIcon
						domain={logoDomain}
						{...ICON_PROPS}
						fallbackIcon={
							isProspect ? (
								<ProspectsIcon {...fallbackIconProps} />
							) : (
								<ClientIcon {...fallbackIconProps} />
							)
						}
					/>
				}
				subtitle={subtitle}
			/>
		</WorkspaceContextProvider>
	);
};
